import Dexie, { type Table } from 'dexie';
import type {
  WorkOrderViewModel,
  WorkOrderDetailViewModel,
  EquipmentViewModel,
  GlobalVariableViewModel,
  SpecificationViewModel,
  WorkOrderChargeViewModel,
  RepairPartViewModel,
  ServiceAgreementChargeTypeViewModel
} from '@api/enterprise/openapi';
import type {
  DcRuntimeViewModel,
  EventDocumentViewModel
} from '@api/bff/openapi/models';

export type StoredWorkOrder = WorkOrderViewModel & {
  _id?: number;
  _lastUpdated?: string;
  _queryKey?: string;
  _userId?: string;
  _totalCount?: number;
};

export type CachedWorkOrder = WorkOrderViewModel & {
  cachedAt: string;
  lastModified?: string;
  version: number;
  _userId?: string;
};

export type CachedWorkOrderDetail = Omit<WorkOrderDetailViewModel, 'id'> & {
  originalId?: string | null;
  cachedAt: string;
  _userId?: string;
};

export type CachedEquipment = EquipmentViewModel & {
  cachedAt: string;
  _userId?: string;
};

export type CachedGlobalVariable = GlobalVariableViewModel & {
  cachedAt: string;
  version: number;
};

export type CachedSpecification = SpecificationViewModel & {
  cachedAt: string;
  _userId?: string;
};

export type CachedCharge = WorkOrderChargeViewModel & {
  cachedAt: string;
  _userId?: string;
};

export type CachedRepairPart = RepairPartViewModel & { cachedAt: string };

export type CachedChargeType = ServiceAgreementChargeTypeViewModel & {
  cachedAt: string;
};

export type CachedCalibrationRuntime = DcRuntimeViewModel & {
  cachedAt: string;
  _userId?: string;
};

export type CachedEventDocument = EventDocumentViewModel & {
  cachedAt: string;
  _userId?: string;
};

export type DownloadMetadata = {
  jobNumber: string;
  status:
    | 'not_cached'
    | 'downloading'
    | 'cached'
    | 'update_available'
    | 'error';
  downloadedAt?: string;
  lastSyncedAt?: string;
  version: number;
  size: number;
  progress?: number;
  error?: string;
  _userId?: string;
};

export type SyncEntityType =
  | 'calibration_save'
  | 'acknowledgment'
  | 'calibration_complete'
  | 'dc_runtime_create'
  | 'wo_detail_complete'
  | 'wo_detail_skip'
  | 'wo_detail_incomplete'
  | 'wo_complete'
  | 'wo_incomplete'
  | 'charge_add'
  | 'charge_delete'
  | 'wo_document_create';

export type SyncQueueItem = {
  id?: number;
  _userId: string;
  entityType: SyncEntityType;
  entityKey: string;
  payload: Record<string, unknown>;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
  priority: number;
  dependsOn?: number[];
  retryCount: number;
  maxRetries: number;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
  nextRetryAt?: string;
};

export type PendingAcknowledgment = {
  id?: number;
  jobNumber: string;
  calibrationRuntimeId: string;
  woDetailGlobalId: string | null;
  printedName: string;
  signatureDataUrl: string;
  timestamp: string;
  createdAt: string;
  retryCount: number;
  error?: string;
  _userId?: string;
};

export type PendingCertGeneration = {
  id?: number;
  eventNum: number;
  calibrationId: string;
  jobNumber: string;
  sequenceNumber: string;
  _userId: string;
  createdAt: string;
};

export type OfflineSessionMarker = {
  key: 'current';
  userName: string;
  email: string;
  employeeId: string;
  employeeCompany: string;
  fullName: string;
  locationName: string;
  profileName: string;
  lastServerValidation: number;
  lastActivity: number;
};

class CalibrationDatabase extends Dexie {
  workOrders!: Table<StoredWorkOrder>;
  cachedWorkOrders!: Table<CachedWorkOrder>;
  cachedWorkOrderDetails!: Table<CachedWorkOrderDetail>;
  cachedEquipment!: Table<CachedEquipment>;
  cachedGlobalVariables!: Table<CachedGlobalVariable>;
  cachedSpecifications!: Table<CachedSpecification>;
  cachedCharges!: Table<CachedCharge>;
  cachedRepairParts!: Table<CachedRepairPart>;
  cachedChargeTypes!: Table<CachedChargeType>;
  cachedCalibrationRuntimes!: Table<CachedCalibrationRuntime>;
  cachedEventDocuments!: Table<CachedEventDocument>;
  downloadMetadata!: Table<DownloadMetadata>;
  syncQueue!: Table<SyncQueueItem>;
  pendingAcknowledgments!: Table<PendingAcknowledgment>;
  pendingCertGenerations!: Table<PendingCertGeneration>;
  offlineSession!: Table<OfflineSessionMarker, string>;

  constructor() {
    super('CalibrationDatabase');

    this.version(1).stores({
      workOrders:
        '++_id, &jobNumber, customerName, status, openDate, expectedDueDate, _queryKey, _userId, _lastUpdated, [_queryKey+_userId]',
      cachedWorkOrders:
        '&jobNumber, customerId, lastModified, cachedAt, version',
      cachedWorkOrderDetails:
        '++id, jobNumber, sequenceNumber, [jobNumber+sequenceNumber], cachedAt',
      cachedEquipment:
        '[companyName+id], companyName, id, serialNumber, cachedAt',
      cachedGlobalVariables: '&name, cachedAt, version',
      cachedSpecifications:
        '++id, [companyName+equipmentId], companyName, equipmentId, cachedAt',
      cachedCharges:
        '++id, jobNumber, sequenceNumber, [jobNumber+sequenceNumber], chargeType, cachedAt',
      cachedCalibrationRuntimes:
        '++id, [company+gageSn+woNum+woDetailSeq], company, woNum, woDetailSeq, cachedAt',
      cachedEventDocuments: '++id, eventNumber, documentId, cachedAt',
      downloadMetadata:
        '&jobNumber, status, downloadedAt, lastSyncedAt, version, size',
      syncQueue: '++id, jobNumber, action, createdAt'
    });

    this.version(2).stores({
      pendingAcknowledgments:
        '++id, jobNumber, calibrationRuntimeId, [company+gageSn+woDetailSeq], createdAt, retryCount'
    });

    this.version(3).stores({
      cachedCalibrationRuntimes:
        '++id, woDetailGlobalId, gagesGlobalId, cachedAt',
      pendingAcknowledgments:
        '++id, jobNumber, calibrationRuntimeId, woDetailGlobalId, createdAt, retryCount'
    });

    this.version(4).stores({
      workOrders:
        '++_id, &jobNumber, customerName, status, openDate, expectedDueDate, _queryKey, _userId, _lastUpdated, [_queryKey+_userId]',
      cachedWorkOrders:
        '&jobNumber, customerId, lastModified, cachedAt, version, _userId',
      cachedWorkOrderDetails:
        '++id, jobNumber, sequenceNumber, [jobNumber+sequenceNumber], cachedAt, _userId',
      cachedEquipment:
        '[companyName+id], companyName, id, serialNumber, cachedAt, _userId',
      cachedGlobalVariables: '&name, cachedAt, version',
      cachedSpecifications:
        '++id, [companyName+equipmentId], companyName, equipmentId, cachedAt, _userId',
      cachedCharges:
        '++id, jobNumber, sequenceNumber, [jobNumber+sequenceNumber], chargeType, cachedAt, _userId',
      cachedCalibrationRuntimes:
        '++id, woDetailGlobalId, gagesGlobalId, cachedAt, _userId',
      cachedEventDocuments: '++id, eventNumber, documentId, cachedAt, _userId',
      downloadMetadata:
        '&jobNumber, status, downloadedAt, lastSyncedAt, version, size, _userId',
      syncQueue:
        '++id, _userId, entityType, entityKey, status, priority, createdAt, [_userId+entityType+entityKey]',
      pendingAcknowledgments:
        '++id, jobNumber, calibrationRuntimeId, woDetailGlobalId, createdAt, retryCount, _userId'
    });

    this.version(5).stores({
      cachedRepairParts: '[companyName+partNumber], companyName, cachedAt',
      cachedChargeTypes: '++id, [branch+group+name], group, cachedAt'
    });

    this.version(6).stores({
      pendingCertGenerations: '++id, eventNum, _userId, createdAt'
    });

    this.version(7).stores({
      offlineSession: '&key'
    });
  }

  async clearOldData(daysToKeep = 7) {
    const cutoffDate = new Date(Date.now() - daysToKeep * 24 * 60 * 60 * 1000);

    await this.workOrders
      .where('_lastUpdated')
      .below(cutoffDate.toISOString())
      .delete();
  }
}

export const db = new CalibrationDatabase();

db.open().catch(error => {
  console.error('Failed to initialize database:', error);
});

export const initializeDatabase = () => db.isOpen() || db.open();
